import { useEffect, useRef, useState } from 'react';
import { engine } from '../hooks/useEngine.ts';
import {
  CamIcon,
  CamOffIcon,
  ChatIcon,
  DiceIcon,
  FlagIcon,
  FlipIcon,
  HeartIcon,
  HourglassIcon,
  MicIcon,
  MicOffIcon,
  NextIcon,
  PhoneOffIcon,
  SoundOffIcon,
  SoundOnIcon,
} from './Icons.tsx';
import { REACTION_EMOJI } from './ReactionLayer.tsx';

interface Props {
  micOn: boolean;
  camOn: boolean;
  soundOn: boolean;
  canFlipCamera: boolean;
  liked: boolean;
  inCall: boolean;
  unread: number;
  chatOpen: boolean;
  hasPrompt: boolean;
  promptOpen: boolean;
  stayInvited: boolean;
  stayed: boolean;
  confirmNext: boolean;
  onToggleChat: () => void;
  /** Cards stacked directly above the bar — ice-breaker, stay invitation. */
  children?: React.ReactNode;
}

const REPORT_REASONS = [
  { id: 'nudity', label: 'Nudity or sexual content' },
  { id: 'harassment', label: 'Harassment or hate' },
  { id: 'minor', label: 'Appears to be a minor' },
  { id: 'spam', label: 'Spam or advertising' },
  { id: 'other', label: 'Something else' },
] as const;

export function ControlBar({
  micOn,
  camOn,
  soundOn,
  canFlipCamera,
  liked,
  inCall,
  unread,
  chatOpen,
  hasPrompt,
  promptOpen,
  stayInvited,
  stayed,
  confirmNext,
  onToggleChat,
  children,
}: Props) {
  const [reporting, setReporting] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!reporting) return;
    const onDown = (event: PointerEvent) => {
      if (!reportRef.current?.contains(event.target as Node)) setReporting(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [reporting]);

  return (
    <div
      className={`pointer-events-none absolute inset-x-0 bottom-0 z-30 flex-col items-center gap-3 px-4 pb-4 ${
        chatOpen ? 'hidden md:flex md:pr-96' : 'flex'
      }`}
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      {children}

      {/* Reaction strip */}
      {inCall && showReactions && (
        <div className="glass pointer-events-auto flex items-center gap-1 rounded-full px-2 py-1.5">
          {REACTION_EMOJI.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => engine.react(emoji)}
              aria-label={`React with ${emoji}`}
              className="rounded-full px-2.5 py-1 text-2xl transition hover:scale-125 active:scale-95"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {/* Report menu */}
      {reporting && (
        <div
          ref={reportRef}
          className="glass pointer-events-auto w-full max-w-xs overflow-hidden rounded-2xl"
        >
          <p className="border-b border-white/10 px-4 py-3 text-xs uppercase tracking-widest text-white/40">
            Report and skip
          </p>
          {REPORT_REASONS.map((reason) => (
            <button
              key={reason.id}
              type="button"
              onClick={() => {
                engine.report(reason.id);
                setReporting(false);
              }}
              className="block w-full px-4 py-3 text-left text-sm text-white/80 transition hover:bg-phidio-500/20"
            >
              {reason.label}
            </button>
          ))}
        </div>
      )}

      <div className="glass pointer-events-auto flex max-w-full items-center gap-1 rounded-full p-1.5 shadow-2xl sm:gap-2 sm:p-2">
        <Round
          label={micOn ? 'Mute microphone' : 'Unmute microphone'}
          active={!micOn}
          onClick={() => engine.toggleMic()}
        >
          {micOn ? <MicIcon /> : <MicOffIcon />}
        </Round>

        <Round
          label={camOn ? 'Turn camera off' : 'Turn camera on'}
          active={!camOn}
          onClick={() => engine.toggleCam()}
        >
          {camOn ? <CamIcon /> : <CamOffIcon />}
        </Round>

        {canFlipCamera && (
          <Round label="Flip camera" onClick={() => void engine.flipCamera()} className="hidden sm:flex">
            <FlipIcon />
          </Round>
        )}

        {/* Next — the whole point of the product */}
        <button
          type="button"
          onClick={() => engine.next()}
          aria-label={confirmNext ? 'Tap again to leave' : 'Next person'}
          title={confirmNext ? 'Tap again to leave' : 'Next person'}
          className={`group relative mx-0.5 flex h-12 shrink-0 items-center gap-1.5 rounded-full px-4 text-sm font-bold uppercase tracking-wider text-white transition active:scale-95 sm:mx-1 sm:h-16 sm:gap-2 sm:px-8 sm:text-base ${
            confirmNext
              ? 'bg-gradient-to-br from-amber-400 to-amber-600 shadow-[0_10px_35px_-10px_#f59e0b]'
              : 'bg-gradient-to-br from-phidio-400 to-phidio-600 shadow-[0_10px_35px_-10px_var(--color-phidio-500)]'
          }`}
        >
          <NextIcon width={18} height={18} />
          {confirmNext ? 'Sure?' : 'Next'}
        </button>

        <Round
          label={stayed ? 'You both stayed' : stayInvited ? 'Waiting for them' : 'Ask them to stay'}
          onClick={() => engine.stay()}
          disabled={!inCall || stayed || stayInvited}
          className={`hidden sm:flex ${
            stayed
              ? 'text-amber-300 disabled:opacity-100'
              : stayInvited
                ? 'text-amber-300/60 disabled:opacity-100'
                : ''
          }`}
        >
          <HourglassIcon />
        </Round>

        <Round
          label={promptOpen ? 'Hide the ice-breaker' : 'Show an ice-breaker'}
          onClick={() => (hasPrompt ? engine.togglePrompt() : engine.newPrompt())}
          active={promptOpen}
          disabled={!inCall}
          className="hidden md:flex"
        >
          <DiceIcon />
        </Round>

        <Round
          label={liked ? 'Already liked' : 'Like this person'}
          onClick={() => engine.like()}
          disabled={!inCall || liked}
          className={liked ? 'text-phidio-400' : ''}
        >
          <HeartIcon filled={liked} />
        </Round>

        <Round label="Chat" onClick={onToggleChat} active={chatOpen}>
          <span className="relative">
            <ChatIcon />
            {unread > 0 && !chatOpen && (
              <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-phidio-500 px-1 text-[10px] font-bold text-white">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </span>
        </Round>

        <Round
          label="Reactions"
          onClick={() => setShowReactions((value) => !value)}
          active={showReactions}
          disabled={!inCall}
          className="hidden sm:flex"
        >
          <span className="text-lg leading-none">😂</span>
        </Round>

        <Round
          label={soundOn ? 'Mute interface sounds' : 'Unmute interface sounds'}
          onClick={() => engine.toggleSound()}
          className="hidden md:flex"
        >
          {soundOn ? <SoundOnIcon /> : <SoundOffIcon />}
        </Round>

        <Round
          label="Report"
          onClick={() => setReporting((value) => !value)}
          disabled={!inCall}
          active={reporting}
          className="hidden sm:flex"
        >
          <FlagIcon />
        </Round>

        <Round label="Leave" onClick={() => engine.stop()} className="hidden text-red-400 sm:flex">
          <PhoneOffIcon />
        </Round>
      </div>

      {/* Mobile overflow row */}
      <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-1.5 sm:hidden">
        <Pill
          onClick={() => engine.stay()}
          disabled={!inCall || stayed || stayInvited}
          className={stayed || stayInvited ? 'text-amber-300 disabled:opacity-60' : ''}
        >
          <HourglassIcon width={15} height={15} /> {stayed ? 'Staying' : 'Stay'}
        </Pill>
        <Pill
          onClick={() => (hasPrompt ? engine.togglePrompt() : engine.newPrompt())}
          disabled={!inCall}
          className={promptOpen ? 'text-phidio-300' : ''}
        >
          <DiceIcon width={15} height={15} /> Topic
        </Pill>
        {canFlipCamera && (
          <Pill onClick={() => void engine.flipCamera()}>
            <FlipIcon width={15} height={15} /> Flip
          </Pill>
        )}
        <Pill onClick={() => setShowReactions((value) => !value)} disabled={!inCall}>
          😂 React
        </Pill>
        <Pill onClick={() => setReporting((value) => !value)} disabled={!inCall}>
          <FlagIcon width={15} height={15} /> Report
        </Pill>
        <Pill onClick={() => engine.stop()} className="text-red-400">
          <PhoneOffIcon width={15} height={15} /> Leave
        </Pill>
      </div>
    </div>
  );
}

function Round({
  children,
  label,
  onClick,
  active,
  disabled,
  className = '',
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition active:scale-90 disabled:opacity-30 sm:h-13 sm:w-13 ${
        active ? 'bg-phidio-500 text-white' : 'bg-white/8 text-white hover:bg-white/16'
      } ${className}`}
    >
      {children}
    </button>
  );
}

function Pill({
  children,
  onClick,
  disabled,
  className = '',
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`glass flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium text-white/80 transition active:scale-95 disabled:opacity-30 ${className}`}
    >
      {children}
    </button>
  );
}
